import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatedModal, modalPanelClass } from "./AnimatedModal";
import { HiOutlineXMark, HiOutlineTrash } from "react-icons/hi2";

interface ConfirmDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  userName: string;
}

const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  userName,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatedModal isOpen={isOpen} onClose={onClose} contentClassName="sm:max-w-md">
      <div className={modalPanelClass}>
        <div className="sm:hidden flex justify-center pt-2 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
        </div>
        <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-100/80 dark:border-gray-700/80 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-red-500/10 text-red-500">
              <HiOutlineTrash className="w-5 h-5" strokeWidth={2} />
            </div>
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
              {t("confirmDeleteTitle") || "Confirm Delete"}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="p-2.5 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-60"
            aria-label={t("close")}
          >
            <HiOutlineXMark className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 sm:p-5 space-y-5">
          <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
            {t("confirmDeleteMessage", { itemName: userName })}
          </p>
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="w-full sm:flex-1 py-2.5 rounded-xl text-sm font-medium border border-gray-200/80 dark:border-gray-600/80 bg-white/60 dark:bg-gray-800/50 text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 transition-colors disabled:opacity-60"
            >
              {t("cancel")}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={loading}
              className="w-full sm:flex-1 py-2.5 rounded-xl text-sm font-medium border border-red-300/60 dark:border-red-800/50 bg-red-50/90 dark:bg-red-900/25 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              {loading ? (
                <span className="w-4 h-4 border-2 border-red-400/40 border-t-red-600 rounded-full animate-spin" />
              ) : (
                <HiOutlineTrash className="w-4 h-4" />
              )}
              {t("delete")}
            </button>
          </div>
        </div>
      </div>
    </AnimatedModal>
  );
};

export default ConfirmDeleteModal;
